/**
 * Pixel ↔ minute conversions for calendar drag & drop.
 *
 * The grid draws one hour per `--slot-h` pixels inside a column overlay whose top edge is
 * the day's first hour. Dragging inverts the same mapping the overlay uses to place cards,
 * so both directions live here and stay in step. Nothing in this file touches the DOM —
 * callers pass in the rects they measured.
 */

import { snapToStep } from "@/lib/calendar/moveValidation";

/** Drop granularity. Matches the 15-minute blocks an hour cell is divided into. */
export const DRAG_SNAP_MIN = 15;

/**
 * Start minute implied by a dragged card's top edge.
 *
 * The card's top is used rather than the pointer, so wherever inside the card the user
 * grabbed, the card lands where they see it — the grab offset is already baked into the
 * translated rect that dnd-kit reports.
 */
export function startMinFromTop(params: {
  /** Top of the dragged card, in viewport pixels. */
  cardTopPx: number;
  /** Top of the target column's overlay, in viewport pixels. */
  overlayTopPx: number;
  /** Rendered height of one hour. */
  hourPx: number;
  /** First minute the grid draws (dayStartHour * 60). */
  dayStartMin: number;
  /** Snap granularity; defaults to the 15-minute grid. */
  step?: number;
}): number {
  const { cardTopPx, overlayTopPx, hourPx, dayStartMin, step = DRAG_SNAP_MIN } = params;
  if (!Number.isFinite(hourPx) || hourPx <= 0) return dayStartMin;
  const rawMin = dayStartMin + ((cardTopPx - overlayTopPx) / hourPx) * 60;
  return snapToStep(rawMin, step);
}

/**
 * Keeps a candidate range inside the drawn day.
 *
 * A card dragged past the last hour would otherwise be committed at a time the grid
 * cannot draw, leaving it invisible until the next reload.
 */
export function clampStartToDay(params: {
  startMin: number;
  durationMin: number;
  dayStartMin: number;
  dayEndMin: number;
}): number {
  const { startMin, durationMin, dayStartMin, dayEndMin } = params;
  const latestStart = Math.max(dayStartMin, dayEndMin - Math.max(1, durationMin));
  return Math.min(Math.max(startMin, dayStartMin), latestStart);
}

/** Offset from the top of a column overlay at which a given minute is drawn. */
export function topPxForMin(startMin: number, dayStartMin: number, hourPx: number): number {
  return ((startMin - dayStartMin) / 60) * hourPx;
}

/** Reads the rendered hour height from `--slot-h`, falling back to the stylesheet default. */
export function readHourPx(): number {
  if (typeof window === "undefined") return 70;
  const v = getComputedStyle(document.documentElement).getPropertyValue("--slot-h")?.trim();
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : 70;
}
